import { TypedDocumentNode, gql } from '@apollo/client';
import { useState } from 'react';
import styled from 'styled-components';

import {
  Currency,
  Rarity,
  SeasonEligibility,
} from '@sorare/core/src/__generated__/globalTypes';
import { Horizontal } from '@sorare/core/src/atoms/layout/flex';
import { useCurrentUserContext } from '@sorare/core/src/contexts/currentUser';
import { useQuery } from '@sorare/core/src/hooks/graphql/useQuery';
import { StatsRarity } from '@sorare/core/src/lib/playerSearchView';

import { SelectRarity } from 'atoms/SelectRarity';
import { SelectSeasonality } from 'atoms/SelectSeasonality';
import { TimeframeSelector } from 'atoms/TimeframeSelector';
import { TIME_FRAME_FROM_TO, Timeframe } from 'lib/timeframe';

import { PriceGraph } from '../PriceGraph';
import type {
  LazyPriceGraphQuery,
  LazyPriceGraphQueryVariables,
} from './__generated__/index.graphql';

const Filters = styled(Horizontal)`
  overflow: auto;
`;

const LAZY_PLAYER_PRICE_GRAPH_QUERY = gql`
  query LazyPriceGraphQuery(
    $playerSlug: String!
    $rarity: Rarity!
    $seasonEligibility: SeasonEligibility
    $from: ISO8601DateTime
    $to: ISO8601DateTime
  ) {
    tokens {
      tokenPrices(
        first: 20
        playerSlug: $playerSlug
        rarity: $rarity
        seasonEligibility: $seasonEligibility
        from: $from
        to: $to
        includePrivateSales: true
      ) {
        id
        ...PriceGraph_tokenPrice
      }
    }
  }
  ${PriceGraph.fragments.tokenPrice}
` as TypedDocumentNode<LazyPriceGraphQuery, LazyPriceGraphQueryVariables>;

const filterableRarities = [
  Rarity.limited,
  Rarity.rare,
  Rarity.super_rare,
  Rarity.unique,
];

type Props = {
  small?: boolean;
  playerSlug: string;
  currency?: Currency;
  filters?: {
    seasonEligibility?: SeasonEligibility;
    withSeasonEligibility?: boolean;
    rarity?: StatsRarity;
    timeframe?: Timeframe;
  };
  withLegend?: boolean;
};

export const LazyPriceGraph = ({
  small,
  playerSlug,
  currency: currencyProps,
  filters,
  withLegend,
}: Props) => {
  const { currency: userCurrency } = useCurrentUserContext();
  const currency = currencyProps || userCurrency;

  const [rarityState, setRarityState] = useState<StatsRarity>(Rarity.limited);
  const [seasonEligibilityState, setSeasonEligibilityState] =
    useState<SeasonEligibility>(SeasonEligibility.IN_SEASON);
  const [timeframeState, setTimeframeState] = useState<Timeframe>(
    Timeframe.MONTH
  );

  const rarity: StatsRarity = filters?.rarity || rarityState;
  const seasonEligibility =
    (filters?.withSeasonEligibility &&
      (filters?.seasonEligibility || seasonEligibilityState)) ||
    undefined;
  const timeframe = filters?.timeframe || timeframeState;

  const { from, to } = TIME_FRAME_FROM_TO[timeframe];

  const { data, loading } = useQuery(LAZY_PLAYER_PRICE_GRAPH_QUERY, {
    variables: {
      playerSlug,
      rarity,
      seasonEligibility,
      to,
      from,
    },
  });

  const hasTimeFilter = !filters?.timeframe && !!timeframe;
  const hasSeasonEligibilityFilter =
    !filters?.seasonEligibility &&
    !!filters?.withSeasonEligibility &&
    !!seasonEligibility;
  const hasRarityFilter = !filters?.rarity && !!rarity;
  const hasFilters =
    hasTimeFilter || hasSeasonEligibilityFilter || hasRarityFilter;

  return (
    <>
      {!!hasFilters && (
        <Filters>
          {!!hasTimeFilter && (
            <div>
              <TimeframeSelector
                value={timeframe}
                onChange={setTimeframeState}
                buttonSize="compact"
              />
            </div>
          )}
          {!!hasSeasonEligibilityFilter && (
            <div>
              <SelectSeasonality
                seasonality={seasonEligibility}
                setSeasonality={s =>
                  setSeasonEligibilityState(s as SeasonEligibility)
                }
                buttonSize="compact"
              />
            </div>
          )}
          {!!hasRarityFilter && (
            <div>
              <SelectRarity
                selectedRarity={rarity}
                onChange={setRarityState}
                rarities={filterableRarities}
                buttonSize="compact"
              />
            </div>
          )}
        </Filters>
      )}
      <PriceGraph
        currency={currency}
        loading={loading}
        tokenPrices={data?.tokens.tokenPrices || []}
        withLegend={withLegend}
        small={small}
        from={from}
        to={to}
      />
    </>
  );
};
